import React, { useState, useContext } from "react";
import axios from "axios";
import { HerracContext } from "../../App";
import { FaCamera, FaUser, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";

function ProfilePictureUpload() {
  const { user, setUser } = useContext(HerracContext);
  
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Zəhmət olmasa şəkil faylı seçin!" });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "Şəkil ölçüsü 5MB-dan çox olmamalıdır!" });
      return;
    }

    setProfileImage(file);
    setMessage({ type: "", text: "" });

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload profile picture
  const handleUploadProfilePicture = async () => {
    if (!profileImage) {
      setMessage({ type: "error", text: "Zəhmət olmasa bir şəkil seçin!" });
      return;
    }

    const formData = new FormData();
    formData.append("file", profileImage);

    try {
      setUploading(true);
      setMessage({ type: "", text: "" });

      const response = await axios.post(
        "https://api.herracim.az/api/user/upload-profile-picture",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      // Update user context
      const updatedUser = {
        ...user,
        profileImageUrl: response.data.imageUrl,
      };
      setUser(updatedUser);

      setMessage({ type: "success", text: "Profil şəkli uğurla yükləndi!" });
      
      // Reset form
      setProfileImage(null);
      setPreviewImage(null);

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Şəkil yüklənmədi:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Şəkil yüklənərkən xəta baş verdi.",
      });
    } finally {
      setUploading(false);
    }
  };

  // Cancel upload
  const handleCancelUpload = () => {
    setProfileImage(null);
    setPreviewImage(null);
    setMessage({ type: "", text: "" });
  };

  // Get current profile image URL
  const getCurrentImageUrl = () => {
    if (user?.profileImageUrl && user.profileImageUrl !== "NULL") {
      return `https://api.herracim.az/${user.profileImageUrl}`;
    }
    return '/images/profile.svg';
  };

  return (
    <div className="profile-picture-upload">
      <div className="profile-picture-upload__header">
        <FaUser className="profile-picture-upload__icon" />
        <h3 className="profile-picture-upload__title">Profil Şəkli</h3>
      </div>

      <div className="profile-picture-upload__body">
        {/* Image Preview */}
        <div className="profile-picture-upload__preview">
          <div className="profile-picture-upload__image-wrapper">
            <img
              src={previewImage || getCurrentImageUrl()}
              alt="Profil"
              className="profile-picture-upload__image"
            />
            <label className="profile-picture-upload__camera-btn" htmlFor="profile-image-input">
              <FaCamera />
              <input
                id="profile-image-input"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                hidden
              />
            </label>
          </div>
          <div className="profile-picture-upload__info">
            <p className="profile-picture-upload__info-title">
              {user?.fullName || user?.email || "İstifadəçi"}
            </p>
            <p className="profile-picture-upload__info-text">
              JPG, PNG formatında, maksimum 5MB
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        {profileImage && (
          <div className="profile-picture-upload__actions">
            <button
              className="profile-picture-upload__btn profile-picture-upload__btn--primary"
              onClick={handleUploadProfilePicture}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="profile-picture-upload__spinner"></span>
                  Yüklənir...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  Şəkili Yüklə
                </>
              )}
            </button>
            <button
              className="profile-picture-upload__btn profile-picture-upload__btn--secondary"
              onClick={handleCancelUpload}
              disabled={uploading}
            >
              Ləğv et
            </button>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div className={`profile-picture-upload__message profile-picture-upload__message--${message.type}`}>
            {message.type === "success" ? <FaCheckCircle /> : <FaExclamationCircle />}
            <span>{message.text}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePictureUpload;